import { BTLocalStorage, BTSessionStorage, LocalStorageKeys } from "types/btStorage";
import { JobStatusTypes } from "types/enum";

import { jobDetailRoute } from "utilities/flatRoutes/job.routes";
import { type IRouteLocationState } from "utilities/flatRoutes/usePreviousLocation/usePreviousLocation.types";
import { getJobPickerState, setJobPickerState } from "utilities/jobPicker/jobPicker";
import { routes } from "utilities/routes";

import { JobStatusFilterTypes } from "commonComponents/entity/jobAccess/JobAccess/JobAccess.api.types";
import { JobPickerFilterTypes } from "commonComponents/utilities/JobPicker/JobPicker.api.types";
import { type StatusTypes } from "commonComponents/utilities/Status/StatusDisplay";

// note: also set in webforms
// value from @screen-lg less var, this value should match a responsive screen size (xs, sm, md, lg, etc)
export const autoCollapseJobpickerWidth = 992;

export const popoverSelector = ".ActionIcon.ant-popover-open";

export function closeDropdowns() {
    // Todo: Major DOM search. This will be a performance hit. We should look into a better approach.
    document
        .querySelectorAll(`.AccountSwitcherDropdown.ant-dropdown-open, ${popoverSelector}`)
        .forEach((el) => (el as HTMLElement).click());
}

export function getAddJobLink(
    inTemplateMode: boolean,
    fromScratch: boolean,
    openCondensed: boolean = false,
): string | IRouteLocationState {
    if (inTemplateMode) {
        return fromScratch
            ? `/JobPickerActions${routes.template.getTemplateDetailsLink(0)}`
            : `/JobPickerActions${routes.template.getTemplateFromTemplateLink()}`;
    }
    return fromScratch
        ? jobDetailRoute.link({ jobId: 0 }, { openCondensed })
        : `/JobPickerActions${routes.job.getJobFromTemplateLink(0, 0, "Not Sold?")}`;
}

export function isResponsiveAutoCollapsed() {
    return window.innerWidth <= autoCollapseJobpickerWidth;
}

export function isUserPreferenceCollapsed() {
    return BTLocalStorage.get("bt-string-jobPickerPreference") === "closed";
}

export function getCollapsed() {
    return isUserPreferenceCollapsed() || isResponsiveAutoCollapsed();
}

/**
 * @deprecated Use routes.jobpicker.selectJob instead
 */
export function selectJobsAndSetJobMode(jobIds: number[], allJobsSelected: boolean) {
    const jobPickerState = getJobPickerState();
    const storageValue = {
        ...jobPickerState,
        selectedJobIds: jobIds,
        isAllJobsSelected: allJobsSelected,
        isTemplateMode: false,
    };
    setJobPickerState(storageValue);
}

export function setJobMode(isTemplateMode: boolean) {
    const jobPickerState = getJobPickerState();
    const storageValue = {
        ...jobPickerState,
        isTemplateMode: isTemplateMode,
    };
    setJobPickerState(storageValue);
}

export function getStatusTypeForJob(status?: JobStatusTypes): StatusTypes {
    switch (status) {
        case JobStatusTypes.Closed:
            return "danger";
        case JobStatusTypes.Presale:
            return "pending";
        case JobStatusTypes.Open:
            return "info";
        case JobStatusTypes.Warranty:
            return "info";
        case JobStatusTypes.Template:
            return "info";
        case JobStatusTypes.Deleted:
            return "emergency";
        default:
            return "";
    }
}

export function getStatusTextForJob(status: JobStatusTypes | undefined): string {
    switch (status) {
        case JobStatusTypes.Closed:
            return "Closed";
        case JobStatusTypes.Presale:
            return "Presale";
        case JobStatusTypes.Open:
            return "Open";
        case JobStatusTypes.Warranty:
            return "Warranty";
        case JobStatusTypes.Deleted:
            return "Deleted";
        default:
            return "";
    }
}

export function getStatusTextForJobFilter(status: JobStatusFilterTypes | undefined): string {
    switch (status) {
        case JobStatusFilterTypes.Closed:
            return "Closed";
        case JobStatusFilterTypes.Presale:
            return "Presale";
        case JobStatusFilterTypes.Open:
            return "Open";
        case JobStatusFilterTypes.Warranty:
            return "Warranty";
        case JobStatusFilterTypes.OpenOrClosed:
            return "Open and Closed";
        default:
            return "";
    }
}

export function getStoredJobsOtherFilter(fallback: string, builderId?: number): string {
    // eslint-disable-next-line no-restricted-globals
    const storedFilterString = localStorage.getItem(
        getJobPickerStorageKey(LocalStorageKeys.CurrentJobPickerFilter, false, builderId),
    );
    if (!storedFilterString) {
        return fallback;
    }
    try {
        const parsed = JSON.parse(storedFilterString) as Record<string, unknown>;
        const value = parsed[JobPickerFilterTypes.Other];
        if (typeof value === "string" || typeof value === "number") {
            return value.toString();
        }
        if (Array.isArray(value)) {
            return value.toString();
        }
        return fallback;
    } catch {
        return fallback;
    }
}

// When changing, don't forget to also update the Webform versions of these functions that are in AdminAutologin.js
export function getJobPickerStorageKey(
    keyBase: string,
    templatesOnly: boolean,
    builderId?: number,
) {
    let currentBuilderId;

    if (builderId) {
        currentBuilderId = builderId;
    } else {
        // eslint-disable-next-line no-restricted-syntax
        currentBuilderId = (window as any).AsyncJobPickerHelper.getCurrentState().builderId;
    }
    if (currentBuilderId) {
        const templateString = templatesOnly ? "1" : "0";
        return `${keyBase}_${currentBuilderId}_${templateString}`;
    }
    return "";
}

export function clearJobPickerKeys(builderId?: number) {
    // JobPicker Filters
    if (builderId !== undefined) {
        // eslint-disable-next-line no-restricted-globals
        localStorage.removeItem(
            getJobPickerStorageKey(LocalStorageKeys.CurrentJobPickerFilter, true, builderId),
        );
        // eslint-disable-next-line no-restricted-globals
        localStorage.removeItem(
            getJobPickerStorageKey(LocalStorageKeys.CurrentJobPickerFilter, false, builderId),
        );
        // JobPicker Search Bar
        // eslint-disable-next-line no-restricted-globals
        localStorage.removeItem(
            getJobPickerStorageKey(LocalStorageKeys.JobPickerKeywordSearch, true, builderId),
        );
        // eslint-disable-next-line no-restricted-globals
        localStorage.removeItem(
            getJobPickerStorageKey(LocalStorageKeys.JobPickerKeywordSearch, false, builderId),
        );
        // JobPicker Filter Id
        // eslint-disable-next-line no-restricted-globals
        localStorage.removeItem(
            getJobPickerStorageKey(LocalStorageKeys.SelectedJobPickerFilterId, true, builderId),
        );
        // eslint-disable-next-line no-restricted-globals
        localStorage.removeItem(
            getJobPickerStorageKey(LocalStorageKeys.SelectedJobPickerFilterId, false, builderId),
        );
    }

    BTLocalStorage.remove("bt-object-dangerousJobPickerState");
    BTSessionStorage.remove("bt-object-dangerousJobPickerState");
}
